import { useMemo } from "react";
import { useParams } from "react-router";
import { UtensilsCrossed } from "lucide-react";
import { generateMenuBoard } from "@beztack/menu-board";
import type { MenuBoardLayout, Screen, Section } from "@beztack/menu-board";
import { Skeleton } from "@/components/ui/skeleton";
import { usePublicMenu } from "@/hooks/use-menu";
import { formatPrice } from "@/lib/format-price";
import type { MenuSection, PublicMenuProduct } from "@/types/menu";

const UPLOADTHING_BASE_URL = "https://paj514wbqe.ufs.sh/f";

type ProductMap = Map<
  string,
  {
    name: string;
    price: number;
    image?: string;
    discountedPrice?: number;
    promotionMinQuantity?: number;
  }
>;

function buildProductMap(products: PublicMenuProduct[]): ProductMap {
  const map: ProductMap = new Map();
  for (const p of products) {
    map.set(p.id, {
      name: p.name,
      price: p.originalPrice,
      image: p.imageKey ? `${UPLOADTHING_BASE_URL}/${p.imageKey}` : undefined,
      discountedPrice: p.discountedPrice ?? undefined,
      promotionMinQuantity: p.promotionMinQuantity ?? undefined,
    });
  }
  return map;
}

function buildBoardLayout(
  sections: MenuSection[],
  products: PublicMenuProduct[],
): MenuBoardLayout | null {
  const categories = sections.map((s) => ({ id: s.id, name: s.name }));
  const menuProducts = products
    .filter((p) => p.sectionId)
    .map((p) => ({
      id: p.id,
      name: p.name,
      price: p.originalPrice,
      categoryId: p.sectionId as string,
      image: p.imageKey ? `${UPLOADTHING_BASE_URL}/${p.imageKey}` : undefined,
    }));

  if (menuProducts.length === 0) return null;

  return generateMenuBoard({
    categories,
    products: menuProducts,
  });
}

function HeroItem({
  item,
  productMap,
  currency,
}: {
  item: { id: string };
  productMap: ProductMap;
  currency: string | null;
}) {
  const product = productMap.get(item.id);
  if (!product) return null;

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-6">
      {product.image ? (
        <img
          alt={product.name}
          className="h-48 w-48 rounded-2xl object-cover shadow-lg"
          src={product.image}
        />
      ) : (
        <div className="flex h-48 w-48 items-center justify-center rounded-2xl bg-white/10">
          <UtensilsCrossed className="h-16 w-16 text-white/30" />
        </div>
      )}
      <div className="text-center">
        <h2 className="font-bold text-3xl text-white">{product.name}</h2>
        <p className="mt-1 font-bold text-4xl text-yellow-400">
          {formatPrice(product.discountedPrice ?? product.price, currency)}
        </p>
        {product.promotionMinQuantity ? (
          <p className="mt-1 text-white/70 text-sm">Desde {product.promotionMinQuantity} uds.</p>
        ) : null}
      </div>
    </div>
  );
}

function GridItem({
  item,
  productMap,
  currency,
}: {
  item: { id: string };
  productMap: ProductMap;
  currency: string | null;
}) {
  const product = productMap.get(item.id);
  if (!product) return null;

  return (
    <div className="flex items-center gap-4 rounded-xl bg-white/5 p-4">
      {product.image ? (
        <img
          alt={product.name}
          className="h-20 w-20 shrink-0 rounded-lg object-cover"
          src={product.image}
        />
      ) : (
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-white/10">
          <UtensilsCrossed className="h-8 w-8 text-white/30" />
        </div>
      )}
      <div className="flex-1">
        <h3 className="font-semibold text-lg text-white">{product.name}</h3>
        <p className="font-bold text-xl text-yellow-400">
          {formatPrice(product.discountedPrice ?? product.price, currency)}
        </p>
        {product.promotionMinQuantity ? (
          <p className="text-white/70 text-xs">Desde {product.promotionMinQuantity} uds.</p>
        ) : null}
      </div>
    </div>
  );
}

function BoardSection({
  section,
  productMap,
  currency,
}: {
  section: Section;
  productMap: ProductMap;
  currency: string | null;
}) {
  if (section.type === "hero" || section.type === "combo-highlight") {
    return (
      <div className="flex flex-col items-center gap-4">
        {section.items.map((item) => (
          <HeroItem currency={currency} item={item} key={item.id} productMap={productMap} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {section.items.map((item) => (
        <GridItem currency={currency} item={item} key={item.id} productMap={productMap} />
      ))}
    </div>
  );
}

function ScreenDisplay({
  screen,
  productMap,
  orgName,
  currency,
}: {
  screen: Screen;
  productMap: ProductMap;
  orgName: string;
  currency: string | null;
}) {
  return (
    <div className="flex h-screen w-screen flex-col bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
      <header className="flex items-center justify-between border-white/10 border-b px-8 py-4">
        <h1 className="font-bold text-lg text-white/80">{orgName}</h1>
        <span className="font-mono text-sm text-white/40">{String(screen.index + 1)}</span>
      </header>
      <main className="flex flex-1 flex-col justify-center gap-6 overflow-hidden px-8 py-6">
        {screen.sections.map((section, i) => (
          <BoardSection
            currency={currency}
            key={`${section.type}-${String(i)}`}
            productMap={productMap}
            section={section}
          />
        ))}
      </main>
    </div>
  );
}

export default function BoardScreenPage() {
  const { orgSlug, screenIndex } = useParams();
  const index = Number(screenIndex ?? "0");
  const { data, error, isLoading } = usePublicMenu(orgSlug);

  const layout = useMemo(() => {
    if (!data?.sections || !data?.products) return null;
    return buildBoardLayout(data.sections, data.products);
  }, [data]);

  const productMap = useMemo(() => {
    if (!data?.products) return new Map() as ProductMap;
    return buildProductMap(data.products);
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-neutral-900">
        <Skeleton className="h-32 w-32 rounded-xl" />
      </div>
    );
  }

  if (error || !data || !layout) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-neutral-900">
        <div className="text-center">
          <h1 className="font-bold text-2xl text-white">Menu Board</h1>
          <p className="mt-2 text-white/60">{error?.message ?? "No menu data available"}</p>
        </div>
      </div>
    );
  }

  const screen = layout.screens.at(index);

  if (!screen) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-neutral-900">
        <div className="text-center">
          <h1 className="font-bold text-2xl text-white">Screen not found</h1>
          <p className="mt-2 text-white/60">
            This menu has {String(layout.screens.length)} screen
            {layout.screens.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ScreenDisplay
      currency={data.organization.currency}
      orgName={data.organization.name}
      productMap={productMap}
      screen={screen}
    />
  );
}
